import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { ApiError, apiRequest } from "../../api/client";
import {
  ConfirmModal,
  EditorModal,
  ErrorText,
  LockedNotice
} from "../catalog/CatalogPages";
import { DataWorkspace } from "../shared/DataWorkspace";
import { FiveMinuteTimeField } from "./FiveMinuteTimeField";

type DayPart = "AM" | "PM";
type Scope = "SCHOOL" | "CLASS" | "TEACHER";

interface TimeSlot {
  id: string;
  day_part: DayPart;
  start_minute: number;
  end_minute: number;
  display_time: string;
  condition_sort_order: number;
  condition_active: boolean;
  timetable_active: boolean;
}

interface TimeSlotList {
  items: TimeSlot[];
  condition_revision: number;
  locked: boolean;
}

interface GridCell {
  weekday: number;
  time_slot_id: string;
  school_blocked: boolean;
  own_blocked: boolean;
  effective_blocked: boolean;
  editable: boolean;
}

interface UnavailabilityGrid {
  scope: Scope;
  target_id: string | null;
  slots: TimeSlot[];
  cells: GridCell[];
  condition_revision: number;
  locked: boolean;
}

interface TeacherItem {
  id: string;
  name: string;
}

interface ClassItem {
  id: string;
  grade_label: string;
  class_name: string;
}

interface CatalogList<T> {
  items: T[];
}

function minuteToInput(value: number): string {
  return `${String(Math.floor(value / 60)).padStart(2, "0")}:${String(value % 60).padStart(2, "0")}`;
}

function inputToMinute(value: string): number {
  const [hours, minutes] = value.split(":").map(Number);
  return hours * 60 + minutes;
}

export function TimeSlotsPage({ projectId }: { projectId: string }): React.JSX.Element {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<TimeSlot | "new" | null>(null);
  const [deleting, setDeleting] = useState<TimeSlot | null>(null);
  const [dayPart, setDayPart] = useState<DayPart>("AM");
  const [startTime, setStartTime] = useState("08:00");
  const [endTime, setEndTime] = useState("08:35");
  const slots = useQuery({
    queryKey: ["time-slots", projectId],
    queryFn: () =>
      apiRequest<TimeSlotList>(`/projects/${projectId}/time-slots`)
  });
  const mutation = useMutation({
    mutationFn: async (action: "save" | "delete") => {
      if (!slots.data) return;
      if (action === "delete" && deleting) {
        return apiRequest(
          `/projects/${projectId}/time-slots/${deleting.id}?expected_condition_revision=${slots.data.condition_revision}`,
          { method: "DELETE" }
        );
      }
      if (!editing) return;
      const path =
        editing === "new"
          ? `/projects/${projectId}/time-slots`
          : `/projects/${projectId}/time-slots/${editing.id}`;
      return apiRequest(path, {
        method: editing === "new" ? "POST" : "PATCH",
        body: JSON.stringify({
          day_part: dayPart,
          start_minute: inputToMinute(startTime),
          end_minute: inputToMinute(endTime),
          expected_condition_revision: slots.data.condition_revision
        })
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["time-slots", projectId] });
      await queryClient.invalidateQueries({ queryKey: ["project", projectId] });
      await queryClient.invalidateQueries({ queryKey: ["unavailability", projectId] });
      setEditing(null);
      setDeleting(null);
    },
    onError: async (error) => {
      if (error instanceof ApiError && error.code === "STALE_VERSION") {
        await queryClient.invalidateQueries({ queryKey: ["time-slots", projectId] });
      }
    }
  });

  function openNew(part: DayPart): void {
    setDayPart(part);
    setStartTime(part === "AM" ? "08:00" : "14:00");
    setEndTime(part === "AM" ? "08:35" : "14:35");
    setEditing("new");
  }

  function openEdit(item: TimeSlot): void {
    setDayPart(item.day_part);
    setStartTime(minuteToInput(item.start_minute));
    setEndTime(minuteToInput(item.end_minute));
    setEditing(item);
  }

  return (
    <DataWorkspace
      projectId={projectId}
      active="time_slots"
      description="以下节次适用于周一至周五。"
    >
      {slots.data?.locked ? <LockedNotice /> : null}
      {slots.isError ? <ErrorText error={slots.error} /> : null}
      {slots.isLoading ? <p className="state-message">正在加载节次…</p> : null}
      {slots.data ? (
        <div className="time-slot-layout">
          {(["AM", "PM"] as const).map((part) => {
            const items = slots.data.items.filter((item) => item.day_part === part);
            return (
              <section className="slot-panel" key={part} aria-labelledby={`slot-${part}`}>
                <header className="slot-panel-heading">
                  <div>
                    <p className="section-kicker">{part === "AM" ? "上午" : "下午"}</p>
                    <h2 id={`slot-${part}`}>{part === "AM" ? "上午节次" : "下午节次"}</h2>
                  </div>
                  <button
                    className="ghost-button"
                    type="button"
                    disabled={slots.data.locked}
                    onClick={() => openNew(part)}
                  >
                    添加节次
                  </button>
                </header>
                {items.length === 0 ? (
                  <p className="inline-empty">尚未设置{part === "AM" ? "上午" : "下午"}节次</p>
                ) : (
                  <ol className="slot-list">
                    {items.map((item, index) => (
                      <li key={item.id}>
                        <span className="slot-number">第 {index + 1} 节</span>
                        <strong>{item.display_time}</strong>
                        <div className="row-actions">
                          <button
                            className="text-button"
                            type="button"
                            disabled={slots.data.locked}
                            onClick={() => openEdit(item)}
                          >
                            修改
                          </button>
                          <button
                            className="text-button danger"
                            type="button"
                            disabled={slots.data.locked}
                            onClick={() => setDeleting(item)}
                          >
                            删除
                          </button>
                        </div>
                      </li>
                    ))}
                  </ol>
                )}
              </section>
            );
          })}
        </div>
      ) : null}
      {editing ? (
        <EditorModal
          title={editing === "new" ? "添加节次" : "修改节次"}
          onClose={() => setEditing(null)}
          onSubmit={() => mutation.mutate("save")}
          pending={mutation.isPending}
          error={mutation.error}
        >
          <label className="field">
            <span>时段</span>
            <select value={dayPart} onChange={(event) => setDayPart(event.target.value as DayPart)}>
              <option value="AM">上午</option>
              <option value="PM">下午</option>
            </select>
          </label>
          <FiveMinuteTimeField
            label="开始时间"
            value={startTime}
            onChange={setStartTime}
          />
          <FiveMinuteTimeField
            label="结束时间"
            value={endTime}
            onChange={setEndTime}
          />
          <p className="field-hint">
            修改节次后，旧课表仍保留原时间网格；新时间会在下次排课成功后用于新课表。
          </p>
        </EditorModal>
      ) : null}
      {deleting ? (
        <ConfirmModal
          title="删除节次"
          description={`删除 ${deleting.display_time} 后，它不再用于新的自动排课；已有课表仍保留原时间位置。`}
          dangerLabel="确认删除"
          onClose={() => setDeleting(null)}
          onConfirm={() => mutation.mutate("delete")}
          pending={mutation.isPending}
          error={mutation.error}
        />
      ) : null}
    </DataWorkspace>
  );
}

export function UnavailabilityPage({
  projectId
}: {
  projectId: string;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const [scope, setScope] = useState<Scope>("SCHOOL");
  const [targetId, setTargetId] = useState("");
  const teachers = useQuery({
    queryKey: ["teachers", projectId],
    queryFn: () =>
      apiRequest<CatalogList<TeacherItem>>(`/projects/${projectId}/teachers`)
  });
  const classes = useQuery({
    queryKey: ["classes", projectId],
    queryFn: () =>
      apiRequest<CatalogList<ClassItem>>(`/projects/${projectId}/classes`)
  });
  const canLoadGrid = scope === "SCHOOL" || Boolean(targetId);
  const grid = useQuery({
    queryKey: ["unavailability", projectId, scope, targetId],
    enabled: canLoadGrid,
    queryFn: () => {
      const target = scope === "SCHOOL" ? "" : `&target_id=${targetId}`;
      return apiRequest<UnavailabilityGrid>(
        `/projects/${projectId}/unavailability?scope=${scope}${target}`
      );
    }
  });
  const mutation = useMutation({
    mutationFn: (cell: GridCell) => {
      if (!grid.data) return Promise.resolve(undefined);
      return apiRequest<UnavailabilityGrid>(
        `/projects/${projectId}/unavailability/cells`,
        {
          method: "PUT",
          body: JSON.stringify({
            scope,
            target_id: scope === "SCHOOL" ? null : targetId,
            weekday: cell.weekday,
            time_slot_id: cell.time_slot_id,
            blocked: !cell.own_blocked,
            expected_condition_revision: grid.data.condition_revision
          })
        }
      );
    },
    onSuccess: async (data) => {
      if (data) {
        queryClient.setQueryData(
          ["unavailability", projectId, scope, targetId],
          data
        );
      }
      await queryClient.invalidateQueries({ queryKey: ["project", projectId] });
    },
    onError: async (error) => {
      if (error instanceof ApiError && error.code === "STALE_VERSION") {
        await queryClient.invalidateQueries({
          queryKey: ["unavailability", projectId]
        });
      }
    }
  });
  const targets = scope === "TEACHER" ? teachers.data?.items : classes.data?.items;
  const weekdayLabels = ["周一", "周二", "周三", "周四", "周五"];

  function changeScope(next: Scope): void {
    setScope(next);
    setTargetId("");
  }

  return (
    <DataWorkspace
      projectId={projectId}
      active="unavailability"
      description="全校设置会叠加到班级和教师。"
    >
      <section className="content-card data-card">
        <div className="data-toolbar">
          <div className="segmented-tabs" aria-label="不排课类型">
            {(["SCHOOL", "CLASS", "TEACHER"] as const).map((item) => (
              <button
                key={item}
                type="button"
                aria-pressed={scope === item}
                onClick={() => changeScope(item)}
              >
                {item === "SCHOOL" ? "全校" : item === "CLASS" ? "班级" : "教师"}不排课
              </button>
            ))}
          </div>
          {scope !== "SCHOOL" ? (
            <label className="target-picker field">
              <span>选择{scope === "CLASS" ? "班级" : "教师"}</span>
              <select value={targetId} onChange={(event) => setTargetId(event.target.value)}>
                <option value="">请选择</option>
                {(targets ?? []).map((item) => (
                  <option value={item.id} key={item.id}>
                    {"name" in item
                      ? item.name
                      : `${item.grade_label}${item.class_name}`}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </div>
        {grid.data?.locked ? <LockedNotice /> : null}
      {grid.isLoading ? <p className="state-message">正在加载不排课时间…</p> : null}
      {grid.isError ? (
        <section className="empty-card compact">
          <h2>暂时无法设置</h2>
          <ErrorText error={grid.error} />
          {grid.error instanceof ApiError && grid.error.code === "TIME_SLOTS_REQUIRED" ? (
            <a className="text-link" href={`/projects/${projectId}/time_slots`}>
              前往设置课程表时间
            </a>
          ) : null}
        </section>
      ) : null}
      {!canLoadGrid ? (
        <section className="empty-card compact">
          <h2>请选择一个{scope === "CLASS" ? "班级" : "教师"}</h2>
        </section>
      ) : null}
      {grid.data ? (
        <>
          <div className="unavailability-legend" aria-label="图例">
            <span><i className="legend-own" />当前页面设置</span>
            {scope !== "SCHOOL" ? <span><i className="legend-school" />全校设置（只读）</span> : null}
          </div>
          <p className="scroll-hint" role="note">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
              <path d="M8 7l-4 5 4 5M16 7l4 5-4 5" />
            </svg>
            左右滑动查看完整网格
          </p>
          <div className="grid-scroll" tabIndex={0}>
            <table className="schedule-grid">
              <thead>
                <tr>
                  <th>节次</th>
                  {weekdayLabels.map((label) => <th key={label}>{label}</th>)}
                </tr>
              </thead>
              <tbody>
                {grid.data.slots.map((slot) => (
                  <tr key={slot.id}>
                    <th>
                      <span>{slot.day_part === "AM" ? "上午" : "下午"}</span>
                      {slot.display_time}
                    </th>
                    {weekdayLabels.map((label, index) => {
                      const cell = grid.data.cells.find(
                        (item) =>
                          item.weekday === index + 1 &&
                          item.time_slot_id === slot.id
                      );
                      if (!cell) return <td key={label} />;
                      const readOnlySchool =
                        scope !== "SCHOOL" && cell.school_blocked;
                      return (
                        <td key={label}>
                          <button
                            className={`availability-cell ${cell.effective_blocked ? "blocked" : ""} ${readOnlySchool ? "school-layer" : ""}`}
                            type="button"
                            disabled={
                              grid.data.locked ||
                              mutation.isPending ||
                              !cell.editable
                            }
                            aria-label={`${label} ${slot.display_time} ${
                              readOnlySchool
                                ? "全校不排课"
                                : cell.own_blocked
                                  ? "设置为排课"
                                  : "设置为不排课"
                            }`}
                            onClick={() => mutation.mutate(cell)}
                          >
                            {readOnlySchool
                              ? "全校不排"
                              : cell.own_blocked
                                ? "不排课"
                                : "可排课"}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ErrorText error={mutation.error} />
        </>
      ) : null}
      </section>
    </DataWorkspace>
  );
}
